/**
 * A chave de API da Cativa de cada marca (A151, 13/10/2026). Coleção
 * `conexoesCativa`, id do documento == id da marca.
 *
 * A integração com a Cativa é por marca: cada uma tem a própria comunidade
 * (o "tenant" da Cativa) e a própria chave, criada pela administração da marca
 * no painel da Cativa (Integrações → API Keys). Com ela a Eva sincroniza as
 * badges e, nas fases seguintes, cria a conta da paciente com a mesma senha.
 * Substitui o e-mail e a senha de uma pessoa administradora da Cativa que o
 * painel antigo pedia a cada sincronização.
 *
 * **Por que fora do documento da marca:** `GET /api/partners/branding` é
 * público e devolve o `cativaConfig` inteiro — a chave ali seria vazamento sem
 * autenticação. Mesmo motivo da coleção do Calendly.
 *
 * A chave é gravada cifrada (`EncryptedValue`, chave de cifra
 * `CATIVA_SECRET_ENCRYPTION_KEY` no Secret Manager) e nunca sai em resposta:
 * use `paraSituacaoDaConexaoCativa()`, o único caminho de saída.
 */
import { z } from 'zod';
import { brandIdField } from './brand';
import { EncryptedValueSchema } from './calendly-connection.schema';

export const ConexaoCativaSchema = z.object({
  id: z.string(),
  brandId: brandIdField(),
  chaveDeApi: EncryptedValueSchema,
  /** Os últimos caracteres da chave, para a tela reconhecer qual está salva. */
  finalDaChave: z.string().max(8),
  /** A comunidade a que a chave pertence, segundo a própria Cativa (`auth/me`). */
  comunidade: z.string(),
  /** Última vez que a Cativa aceitou a chave. */
  validadaEm: z.coerce.date(),
  configuradaPor: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type ConexaoCativa = z.infer<typeof ConexaoCativaSchema>;

/** O que a tela vê: se há chave, o final dela e a comunidade. Nunca a chave. */
export interface SituacaoDaConexaoCativa {
  configurada: boolean;
  finalDaChave: string | null;
  comunidade: string | null;
  validadaEm: string | null;
  configuradaEm: string | null;
}

export function paraSituacaoDaConexaoCativa(c: ConexaoCativa | null | undefined): SituacaoDaConexaoCativa {
  if (!c || c.deletedAt) return { configurada: false, finalDaChave: null, comunidade: null, validadaEm: null, configuradaEm: null };
  return {
    configurada: true,
    finalDaChave: c.finalDaChave,
    comunidade: c.comunidade,
    validadaEm: new Date(c.validadaEm).toISOString(),
    configuradaEm: new Date(c.updatedAt).toISOString(),
  };
}

/**
 * A sessão da paciente na comunidade da Cativa da marca (A152). Coleção
 * `sessoesCativa`, id `<idDaConta>_<idDaMarca>`.
 *
 * O feed, os comentários, as reações e as publicações são feitos em nome
 * dela, com o token dela na Cativa — a chave de API da marca publicaria em
 * nome do dono da chave. Antes, o token ficava no navegador e ia em todo
 * pedido ao proxy; agora fica no servidor, cifrado como a chave da marca, e
 * é renovado sozinho (o de acesso dura cerca de uma hora).
 */
export const SessaoCativaSchema = z.object({
  id: z.string(),
  userId: z.string(),
  brandId: brandIdField(),
  cativaUserId: z.string().nullable().optional(),
  /**
   * A Eva criou esta conta na Cativa, com a mesma senha da Eva (A153). Só
   * nessas a troca de senha da Eva é repassada à Cativa — numa conta que já
   * existia lá, a senha é dela, não da Eva.
   */
  contaCriadaPelaEva: z.boolean().optional().default(false),
  /** A badge de momento que a Eva deu (A153), para trocar quando o momento mudar. */
  badgeDoMomento: z.string().nullable().optional(),
  accessToken: EncryptedValueSchema,
  refreshToken: EncryptedValueSchema,
  vinculadaEm: z.coerce.date(),
  renovadaEm: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type SessaoCativa = z.infer<typeof SessaoCativaSchema>;
